import axios, { AxiosResponse } from 'axios';
import { base_url } from '../ApiConst';
import { STORAGE_CONSTANTS } from '../../shared/Constants/Constants';
import {
  BulkUploadHistoryParams,
  BulkUploadHistoryResponse,
  BulkUploadJobStatusResponse,
  QueueBulkUploadResponse,
  QueueMasterBulkUploadPayload,
  QueueStoreBulkUploadPayload,
  TemplateFileType,
} from '../../types/bulkUpload';

/**
 * Returns clean base URL without trailing slash
 */
const getCleanBaseUrl = (): string => {
  return (base_url || '').replace(/\/+$/, '');
};

/**
 * Retrieves user authorization token from argument or localStorage
 */
export const getAuthToken = (customToken?: string): string => {
  if (customToken) return customToken;
  try {
    return localStorage.getItem(STORAGE_CONSTANTS.USER_TOKEN) || '';
  } catch {
    return '';
  }
};

/**
 * Resolves a full URL for error logs or media files that may come back
 * as relative paths from the backend (e.g. /media/bulk_uploads/errors/...)
 */
export const resolveMediaUrl = (pathOrUrl: string | null): string | null => {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }
  const cleanBase = getCleanBaseUrl();
  const cleanPath = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${cleanBase}${cleanPath}`;
};

/**
 * Calculates progress percentage according to the specification:
 * Math.round(((created_rows + failed_rows) / total_rows) * 100)
 */
export const calculateJobProgress = (
  totalRows: number,
  createdRows: number,
  failedRows: number
): number => {
  if (!totalRows || totalRows <= 0) return 0;
  const processed = (createdRows || 0) + (failedRows || 0);
  const percent = Math.round((processed / totalRows) * 100);
  return Math.min(100, Math.max(0, percent));
};

export const formatAuthHeader = (tok?: string): string => {
  if (!tok) return '';
  return tok.startsWith('Token ') ? tok : `Token ${tok}`;
};

/**
 * 3.1. Download Sample Template
 * GET /api/v1/store/product/bulk_upload/?file_type=csv|excel
 * Returns raw binary Blob stream
 */
export const downloadSampleTemplate = async (
  fileType: TemplateFileType = 'csv',
  customToken?: string
): Promise<Blob> => {
  const token = getAuthToken(customToken);
  const url = `${getCleanBaseUrl()}/api/v1/store/product/bulk_upload/`;

  const response: AxiosResponse<Blob> = await axios.get(url, {
    params: { file_type: fileType },
    responseType: 'blob',
    headers: {
      Authorization: formatAuthHeader(token),
    },
  });

  return response.data;
};

/**
 * Helper to download and save sample template file in the browser
 */
export const triggerSampleTemplateDownload = async (
  fileType: TemplateFileType = 'csv',
  customToken?: string
): Promise<string> => {
  const blob = await downloadSampleTemplate(fileType, customToken);
  const filename =
    fileType === 'excel'
      ? 'Sample_File_Product_Bulk_upload.xlsx'
      : 'Sample_File_Product_Bulk_upload.csv';

  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);

  return filename;
};

/**
 * 3.2. Queue Store Product Bulk Upload
 * POST /api/v1/store/product/bulk_upload/
 * Content-Type: multipart/form-data
 * Keys: my_file (REQUIRED), store_id (REQUIRED), zip_media (OPTIONAL)
 */
export const queueStoreProductBulkUpload = async (
  payload: QueueStoreBulkUploadPayload,
  customToken?: string
): Promise<QueueBulkUploadResponse> => {
  const { file, storeId, zipMedia } = payload;

  if (!file) {
    throw new Error('Spreadsheet file is required.');
  }

  const fileName = file.name.toLowerCase();
  if (!fileName.endsWith('.csv') && !fileName.endsWith('.xlsx')) {
    throw new Error(
      "Invalid file type. Only CSV (.csv) and Excel (.xlsx) files are accepted."
    );
  }

  if (!storeId) {
    throw new Error('Target Store ID is required for bulk upload.');
  }

  if (zipMedia) {
    const zipName = zipMedia.name.toLowerCase();
    if (!zipName.endsWith('.zip')) {
      throw new Error("Invalid media archive format. Only ZIP (.zip) files are accepted.");
    }
  }

  const formData = new FormData();
  // Form-data key must be exactly 'my_file' per backend specification
  formData.append('my_file', file);
  formData.append('store_id', String(storeId));
  if (zipMedia) {
    formData.append('zip_media', zipMedia);
  }

  const token = getAuthToken(customToken);
  const url = `${getCleanBaseUrl()}/api/v1/store/product/bulk_upload/`;

  const response: AxiosResponse<QueueBulkUploadResponse> = await axios.post(
    url,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: formatAuthHeader(token),
      },
    }
  );

  return response.data;
};

/**
 * 3.3. Poll Bulk Upload Job Status
 * GET /api/v1/store/product/bulk_upload/status/{job_id}/
 */
export const pollBulkUploadJobStatus = async (
  jobId: number | string,
  customToken?: string
): Promise<BulkUploadJobStatusResponse> => {
  if (!jobId) {
    throw new Error('job_id is required to check job status.');
  }

  const token = getAuthToken(customToken);
  const url = `${getCleanBaseUrl()}/api/v1/store/product/bulk_upload/status/${jobId}/`;

  const response: AxiosResponse<BulkUploadJobStatusResponse> = await axios.get(
    url,
    {
      headers: {
        Authorization: formatAuthHeader(token),
      },
    }
  );

  return response.data;
};

/**
 * 3.4. List Bulk Upload Job History
 * GET /api/v1/store/product/bulk_upload/history/
 */
export const getBulkUploadHistory = async (
  params?: BulkUploadHistoryParams,
  customToken?: string
): Promise<BulkUploadHistoryResponse> => {
  const token = getAuthToken(customToken);
  const url = `${getCleanBaseUrl()}/api/v1/store/product/bulk_upload/history/`;

  const queryParams: Record<string, any> = {
    page: params?.page || 1,
    limit: Math.min(params?.limit || 20, 100),
  };

  if (params?.upload_type) {
    queryParams.upload_type = params.upload_type;
  }

  const response: AxiosResponse<BulkUploadHistoryResponse> = await axios.get(
    url,
    {
      params: queryParams,
      headers: {
        Authorization: formatAuthHeader(token),
      },
    }
  );

  return response.data;
};

/**
 * 3.5. Master Product Bulk Upload (Platform Admin / Superuser)
 * POST /products/master-product-bulk-upload/
 */
export const queueMasterProductBulkUpload = async (
  payload: QueueMasterBulkUploadPayload,
  customToken?: string
): Promise<QueueBulkUploadResponse> => {
  const { file, zipMedia } = payload;

  if (!file) {
    throw new Error('Master product spreadsheet file is required.');
  }

  const formData = new FormData();
  formData.append('my_file', file);
  if (zipMedia) {
    formData.append('zip_media', zipMedia);
  }

  const token = getAuthToken(customToken);
  const url = `${getCleanBaseUrl()}/products/master-product-bulk-upload/`;

  const response: AxiosResponse<QueueBulkUploadResponse> = await axios.post(
    url,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: formatAuthHeader(token),
      },
    }
  );

  return response.data;
};

export interface PollingOptions {
  onProgress?: (status: BulkUploadJobStatusResponse, progressPct: number) => void;
  onSuccess?: (status: BulkUploadJobStatusResponse) => void;
  onFailure?: (status: BulkUploadJobStatusResponse) => void;
  onTimeout?: () => void;
  onError?: (error: any) => void;
  customToken?: string;
  timeoutMs?: number;
}

/**
 * Executes the polling algorithm recommended in Section 7 of the specification:
 * 1. Initial Delay: 2 seconds before the first poll
 * 2. Frequency: Poll every 2.5 seconds for first 30 seconds
 * 3. Backoff: If still processing after 30 seconds, increase interval to 5 seconds
 * 4. Timeout: Stop after 15 minutes and trigger onTimeout
 *
 * Returns a cancel function to stop polling immediately.
 */
export const startJobPolling = (
  jobId: number | string,
  options: PollingOptions = {}
): (() => void) => {
  let isCancelled = false;
  let timerId: ReturnType<typeof setTimeout> | null = null;
  const startTime = Date.now();
  const maxTimeout = options.timeoutMs || 15 * 60 * 1000; // 15 minutes

  const poll = async () => {
    if (isCancelled) return;

    const elapsed = Date.now() - startTime;
    if (elapsed >= maxTimeout) {
      if (options.onTimeout) options.onTimeout();
      return;
    }

    try {
      const statusData = await pollBulkUploadJobStatus(jobId, options.customToken);

      if (isCancelled) return;

      const progress = calculateJobProgress(
        statusData.total_rows,
        statusData.created_rows,
        statusData.failed_rows
      );

      if (options.onProgress) {
        options.onProgress(statusData, progress);
      }

      if (statusData.status === 'completed') {
        if (options.onSuccess) options.onSuccess(statusData);
        return;
      }

      if (statusData.status === 'failed') {
        if (options.onFailure) options.onFailure(statusData);
        return;
      }

      const nextInterval = elapsed <= 30000 ? 2500 : 5000;
      timerId = setTimeout(poll, nextInterval);
    } catch (err) {
      if (isCancelled) return;
      if (options.onError) {
        options.onError(err);
      }
      const nextInterval = elapsed <= 30000 ? 2500 : 5000;
      timerId = setTimeout(poll, nextInterval);
    }
  };

  // Initial delay: 2 seconds before first poll
  timerId = setTimeout(poll, 2000);

  return () => {
    isCancelled = true;
    if (timerId) clearTimeout(timerId);
  };
};
